import {
  Upload,
  Search,
  Sparkles,
  FileSignature,
  FileText,
  Receipt,
  Calendar,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { Seo } from "../lib/head";
import { RelatedLinks } from "../components/marketing";

// Capability grid — deliberately reframed to describe WHAT the platform
// delivers and the guarantees it makes, NOT the internal pipeline sequencing
// or implementation. The prior version read as a numbered architecture
// blueprint (de-id-before-AI ordering, model-isolation approach, signature
// crypto) — competitive-intelligence a public marketing page should not hand
// out. Sell the outcome, not the mechanism.
const FLOW = [
  {
    icon: Upload,
    title: "Bring in your charts",
    body: "Upload facility charts in the formats you already use — access-controlled and fully audit-logged from the moment they land.",
  },
  {
    icon: Search,
    title: "PHI stays protected",
    body: "Resident identifiers are protected by controls enforced in code and built to HIPAA Safe Harbor standards, with every access logged.",
  },
  {
    icon: Sparkles,
    title: "Evidence-backed recommendations",
    body: "Recommendations come with citations you can verify — drafted with AI assistance and grounded in clinical evidence, not model guesswork.",
  },
  {
    icon: FileText,
    title: "Review-ready bundles",
    body: "Polished recommendation packets with citations and facility-level summaries, ready for medical-director review.",
  },
  {
    icon: FileSignature,
    title: "Defensible sign-off",
    body: "Medical directors sign right in the platform, producing a tamper-evident, timestamped record that stands up to audit.",
  },
  {
    icon: Receipt,
    title: "Billing that writes itself",
    body: "Draft invoices straight from your time entries and completed work. Line items stay facility-level — never any PHI.",
  },
  {
    icon: Calendar,
    title: "Never miss a deadline",
    body: "Sync review deadlines to Google or Outlook, with license and filing reminders that keep you ahead of compliance dates.",
  },
];

export function Platform() {
  return (
    <>
      <Seo title="Chart Review Automation Platform | WeConsult Rx" description="Chart review automation for consultant pharmacists — from chart intake to signed, citation-backed recommendations to invoicing, with HIPAA-grade PHI protection and audit-ready sign-off." path="/platform" />
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Platform</Eyebrow>
            <Heading level={1} className="mt-3">
              One workflow, end to end.
            </Heading>
            <Lede className="mt-6">
              From chart intake to signed recommendation bundle to invoiced
              billable hour &mdash; with PHI protected at every step.
            </Lede>
            <div className="mt-8 flex gap-3">
              <LinkButton to="/demo">Request a demo</LinkButton>
              <LinkButton to="/features" variant="secondary">
                Feature deep-dive
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section className="pt-8" tone="soft">
        <Container>
          <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label="Platform capabilities">
            {FLOW.map((f) => {
              const Icon = f.icon;
              return (
                <Card as="li" key={f.title}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-ink-50 text-ink-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-4">
                    {f.title}
                  </Heading>
                  <p className="mt-2 text-ink-600 text-sm">{f.body}</p>
                </Card>
              );
            })}
          </ul>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>Built for healthcare</Eyebrow>
              <Heading level={2} className="mt-2">
                Compliance and clinical rigor, by design.
              </Heading>
              <p className="mt-4 text-ink-600">
                Multi-tenant isolation, encryption at rest and in transit, and
                HIPAA-aligned controls are part of the product — not an
                afterthought.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-ink-800">
                {[
                  "Tenant-scoped data with role-based access",
                  "De-identified AI prompts — no LLM extraction of clinical facts",
                  "In-app medical director sign-off, with a tamper-evident attestation",
                  "Opt-in email and SMS reminders",
                  "Invoice drafting from time entries",
                  "Append-only audit log with long-term retention",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <Badge tone="neutral" className="bg-white/15 border-white/20 text-white">
                What you get
              </Badge>
              <div className="mt-6 grid gap-3 text-sm">
                {[
                  ["Chart review", "Ingest → flag → pharmacist decision"],
                  ["Recommendations", "Evidence-cited, auditable bundles"],
                  ["Sign-off", "Embedded e-signature workflow"],
                  ["Billing", "Time tracking + invoice drafts"],
                  ["Compliance", "Audit trail on every action"],
                  ["Integrations", "Invoicing · calendar sync · EHR ingest"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3"
                  >
                    <span className="font-semibold">{k}</span>
                    <span className="text-white/85 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="mt-12 text-center">
            <LinkButton to="/security" variant="secondary">
              Security overview
              <ArrowRight className="h-4 w-4" aria-hidden />
            </LinkButton>
          </div>
        </Container>
      </Section>
      <RelatedLinks
        items={[
          { to: "/medication-regimen-review-software", label: "MRR software", desc: "The monthly review, chart ingest to invoice." },
          { to: "/consultant-pharmacist-software", label: "Consultant pharmacist software", desc: "SNF, ASC, and dialysis on one platform." },
          { to: "/pricing", label: "Pricing", desc: "Plans from solo to multi-facility." },
        ]}
      />
    </>
  );
}
